import {
  closedRingPositions,
  nearestEdgeIndex,
} from "portal/utils/boundaryEdit";
import {
  DRAFT_POINT_BASE_PROPS,
  DraftPointFeature,
  getDraftPointFeature,
  GHOST_POINT_BASE_PROPS,
  GhostPointFeature,
} from "common/utils/mapEditor";
import { Expression } from "mapbox-gl";
import { FeatureCollection, Geometry } from "geojson";
import { FixType } from "protos/geo/geo";
import {
  InteractionLayer,
  LayerConfig,
  MapImageId,
} from "common/components/map/layers/types";
import { Layer, Source } from "react-map-gl";
import { resolvedTheme as theme } from "common/theme/tailwind.theme";
import destination from "@turf/destination";
import React, { FC, useMemo } from "react";

// separate from FarmLayers: re-deriving FarmMapData per drag frame is far too slow

const SOURCE_ID = "boundary-draft";
const GHOST_SOURCE_ID = "boundary-draft-ghost";

export enum DraftLayerId {
  Fill = "boundary-draft-fill",
  Outline = "boundary-draft-outline",
  PivotArm = "boundary-draft-pivot-arm",
  PivotArmTip = "boundary-draft-pivot-arm-tip",
  ActiveHalo = "boundary-draft-active-halo",
  Points = "boundary-draft-points",
  Labels = "boundary-draft-labels",
  PreviewReplaced = "boundary-draft-preview-replaced",
  PreviewEdges = "boundary-draft-preview-edges",
  Ghost = "boundary-draft-ghost",
  // wider than the dot so a click lands even when the dot lags a frame
  GhostHit = "boundary-draft-ghost-hit",
}

export interface DraftLayerPoint {
  pointId: string;
  lat: number;
  lng: number;
  ordinal?: number;
  fixType?: FixType;
}

export interface LngLat {
  lng: number;
  lat: number;
}

const pointIconByFixType: Expression = [
  "case",
  ["==", ["get", "fixType"], FixType.RTK_FIXED],
  MapImageId.RtkFixed,
  ["==", ["get", "fixType"], FixType.RTK_FLOAT],
  MapImageId.RtkFloat,
  MapImageId.GpsPoint,
];

// a new pivot's arm points north until its tip is dragged
const DEFAULT_PIVOT_ARM_BEARING_DEGREES = 0;

// the arm tip is a drag handle, not a point; marked so the editor can tell
const PIVOT_ARM_TIP_PROPS = {
  ...GHOST_POINT_BASE_PROPS,
  isPivotArmTip: true,
};

export const isPivotArmTipFeature = (f?: { properties: unknown }): boolean =>
  Boolean(
    f?.properties &&
      (f.properties as { isPivotArmTip?: unknown }).isPivotArmTip === true
  );

const EMPTY: FeatureCollection<Geometry> = {
  type: "FeatureCollection",
  features: [],
};

interface BoundaryDraftLayerProps {
  points: DraftLayerPoint[];
  activePointId?: string;
  cursor?: LngLat;
  /** drawn at the cursor rather than at its stored position */
  carriedPointId?: string;
  showInsertPreview?: boolean;
  /** drawn as a new center pivot's arm from its one point */
  pivotArmLengthMeters?: number;
  pivotArmBearingDegrees?: number;
}

const BoundaryDraftLayer: FC<BoundaryDraftLayerProps> = ({
  points: storedPoints,
  activePointId,
  cursor,
  carriedPointId,
  showInsertPreview = false,
  pivotArmLengthMeters,
  pivotArmBearingDegrees = DEFAULT_PIVOT_ARM_BEARING_DEGREES,
}) => {
  const points = useMemo(
    () =>
      carriedPointId && cursor
        ? storedPoints.map((pt) =>
            pt.pointId === carriedPointId
              ? { ...pt, lng: cursor.lng, lat: cursor.lat }
              : pt
          )
        : storedPoints,
    [storedPoints, carriedPointId, cursor]
  );

  const data = useMemo<FeatureCollection<Geometry>>(() => {
    const features: FeatureCollection<Geometry>["features"] = points.map(
      (pt): DraftPointFeature => ({
        type: "Feature",
        id: pt.pointId,
        geometry: { type: "Point", coordinates: [pt.lng, pt.lat] },
        properties: {
          ...DRAFT_POINT_BASE_PROPS,
          pointId: pt.pointId,
          // omitted, not undefined, so the label layer's `has` filter skips it
          ...(pt.ordinal === undefined ? {} : { ordinal: pt.ordinal }),
          fixType: pt.fixType ?? FixType.NO_FIX,
        },
      })
    );
    if (points.length >= 3) {
      features.push({
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [closedRingPositions(points)],
        },
        properties: { isRing: true },
      });
    }
    const [center] = points;
    if (points.length === 1 && center && pivotArmLengthMeters) {
      const end = destination(
        [center.lng, center.lat],
        pivotArmLengthMeters,
        pivotArmBearingDegrees,
        { units: "meters" }
      );
      features.push(
        {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: [[center.lng, center.lat], end.geometry.coordinates],
          },
          properties: { isPivotArm: true },
        },
        {
          type: "Feature",
          geometry: end.geometry,
          properties: { ...PIVOT_ARM_TIP_PROPS },
        }
      );
    }
    return { type: "FeatureCollection", features };
  }, [points, pivotArmLengthMeters, pivotArmBearingDegrees]);

  const ghostData = useMemo<FeatureCollection<Geometry>>(() => {
    if (!cursor) {
      return EMPTY;
    }
    const { lng, lat } = cursor;
    const ghost: GhostPointFeature = {
      type: "Feature",
      geometry: { type: "Point", coordinates: [lng, lat] },
      properties: { ...GHOST_POINT_BASE_PROPS },
    };
    const index = showInsertPreview
      ? nearestEdgeIndex(points, lng, lat)
      : undefined;
    const from = index === undefined ? undefined : points[index];
    const to =
      index === undefined ? undefined : points[(index + 1) % points.length];
    if (!from || !to) {
      return { type: "FeatureCollection", features: [ghost] };
    }
    if (carriedPointId) {
      return { type: "FeatureCollection", features: [ghost] };
    }
    return {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: [
              [from.lng, from.lat],
              [to.lng, to.lat],
            ],
          },
          properties: { replaced: true },
        },
        {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: [
              [from.lng, from.lat],
              [lng, lat],
              [to.lng, to.lat],
            ],
          },
          properties: { replaced: false },
        },
        ghost,
      ],
    };
  }, [points, cursor, showInsertPreview, carriedPointId]);

  const activeFilter = ["==", ["get", "pointId"], activePointId ?? ""];
  const isGhostFilter = ["==", ["geometry-type"], "Point"];

  return (
    <>
      <Source id={SOURCE_ID} type="geojson" data={data}>
        <Layer
          id={DraftLayerId.Fill}
          type="fill"
          filter={["==", ["get", "isRing"], true]}
          paint={{
            "fill-color": theme.colors.carbon.map.farms.focused,
            "fill-opacity": 0.2,
          }}
        />
        <Layer
          id={DraftLayerId.Outline}
          type="line"
          filter={["==", ["get", "isRing"], true]}
          paint={{
            "line-color": theme.colors.carbon.map.farms.focused,
            "line-width": 2,
          }}
        />
        <Layer
          id={DraftLayerId.PivotArm}
          type="line"
          filter={["==", ["get", "isPivotArm"], true]}
          layout={{ "line-cap": "round" }}
          paint={{
            "line-color": theme.colors.carbon.map.farms.focused,
            "line-width": 4,
          }}
        />
        <Layer
          id={DraftLayerId.ActiveHalo}
          type="circle"
          filter={activeFilter}
          paint={{
            "circle-radius": 14,
            "circle-color": theme.colors.blue[600],
            "circle-opacity": 1,
          }}
        />
        <Layer
          id={DraftLayerId.Points}
          type="symbol"
          filter={["has", "pointId"]}
          layout={{
            "icon-image": pointIconByFixType,
            "icon-size": 0.08,
            "icon-allow-overlap": true,
          }}
        />
        <Layer
          id={DraftLayerId.Labels}
          type="symbol"
          filter={["has", "ordinal"]}
          layout={{
            "text-field": ["get", "ordinal"],
            "text-size": 11,
            "text-offset": [0, 1.1],
            "text-allow-overlap": true,
          }}
          paint={{
            "text-color": theme.colors.white,
            "text-halo-color": theme.colors.black,
            "text-halo-width": 1.5,
          }}
        />
        <Layer
          id={DraftLayerId.PivotArmTip}
          type="circle"
          filter={["==", ["get", "isPivotArmTip"], true]}
          paint={{
            "circle-radius": 7,
            "circle-color": theme.colors.white,
            "circle-stroke-color": theme.colors.carbon.map.farms.focused,
            "circle-stroke-width": 3,
          }}
        />
      </Source>
      {/* below the points, so a point under the cursor wins the hit-test */}
      <Source id={GHOST_SOURCE_ID} type="geojson" data={ghostData}>
        <Layer
          id={DraftLayerId.PreviewReplaced}
          beforeId={DraftLayerId.ActiveHalo}
          type="line"
          filter={["==", ["get", "replaced"], true]}
          paint={{
            "line-color": theme.colors.red[500],
            "line-width": 2,
            "line-dasharray": [2, 2],
          }}
        />
        <Layer
          id={DraftLayerId.PreviewEdges}
          beforeId={DraftLayerId.ActiveHalo}
          type="line"
          filter={["==", ["get", "replaced"], false]}
          paint={{
            "line-color": theme.colors.white,
            "line-width": 2,
            "line-dasharray": [2, 2],
          }}
        />
        <Layer
          id={DraftLayerId.GhostHit}
          beforeId={DraftLayerId.ActiveHalo}
          type="circle"
          filter={isGhostFilter}
          paint={{
            "circle-radius": 12,
            "circle-opacity": 0,
          }}
        />
        {!carriedPointId && (
          <Layer
            id={DraftLayerId.Ghost}
            beforeId={DraftLayerId.ActiveHalo}
            type="circle"
            filter={isGhostFilter}
            paint={{
              "circle-radius": 5,
              "circle-color": theme.colors.white,
              "circle-stroke-color": theme.colors.black,
              "circle-stroke-width": 1,
            }}
          />
        )}
      </Source>
    </>
  );
};

const clickPosition = (
  f: { geometry: Geometry },
  lng?: number,
  lat?: number
): LngLat | undefined => {
  if (lng !== undefined && lat !== undefined) {
    return { lng, lat };
  }
  if (f.geometry.type !== "Point") {
    return;
  }
  const [pLng, pLat] = f.geometry.coordinates;
  return pLng === undefined || pLat === undefined
    ? undefined
    : { lng: pLng, lat: pLat };
};

export const useBoundaryDraftLayers = ({
  pointCursor = "grab",
  ghostCursor = "copy",
  onPointClick,
  onPointDragStart,
  onPointDrag,
  onPointDragEnd,
  onGhostClick,
  onPivotArmDrag,
  onPivotArmDragEnd,
}: {
  pointCursor?: string;
  ghostCursor?: string;
  onPointClick?: (feature: DraftPointFeature, position?: LngLat) => void;
  /** return false to leave the point where it is and let the map pan */
  onPointDragStart?: (feature: DraftPointFeature) => boolean | void;
  onPointDrag?: (position: LngLat) => void;
  onPointDragEnd?: (position: LngLat) => void;
  onGhostClick?: (position: LngLat) => void;
  onPivotArmDrag?: (position: LngLat) => void;
  onPivotArmDragEnd?: (position: LngLat) => void;
}): LayerConfig<BoundaryDraftLayerProps> => {
  const interactionLayers: InteractionLayer[] = [
    {
      id: DraftLayerId.PivotArmTip,
      cursor: "grab",
      onDragStart: () => Boolean(onPivotArmDrag),
      onDrag: (lng, lat) => onPivotArmDrag?.({ lng, lat }),
      onDragEnd: (lng, lat) => onPivotArmDragEnd?.({ lng, lat }),
    },
    {
      id: DraftLayerId.Points,
      cursor: pointCursor,
      onClick: (f, lng, lat) => {
        const point = getDraftPointFeature(f);
        if (point) {
          onPointClick?.(point, clickPosition(point, lng, lat));
        }
      },
      onDragStart: (f) => {
        const point = getDraftPointFeature(f);
        return point ? onPointDragStart?.(point) : false;
      },
      onDrag: (lng, lat) => onPointDrag?.({ lng, lat }),
      onDragEnd: (lng, lat) => onPointDragEnd?.({ lng, lat }),
    },
    {
      id: DraftLayerId.GhostHit,
      cursor: ghostCursor,
      deferClick: true,
      onClick: (f, lng, lat) => {
        const position = f && clickPosition(f, lng, lat);
        if (position) {
          onGhostClick?.(position);
        }
      },
    },
  ];
  return {
    interactionLayers,
    idsToLoad: [MapImageId.GpsPoint, MapImageId.RtkFixed, MapImageId.RtkFloat],
    Layers: BoundaryDraftLayer,
  };
};
